import moment from "moment"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { Components, momentLocalizer, View } from "react-big-calendar"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Grid, Tab, Typography, useTheme } from "@mui/material"
import "moment/locale/sv"
import "moment/locale/da"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import CalendarListView from "./components/CalendarListView"
import { CalendarContext } from "./context/CalendarStateProvider"
import {
  ShortTypography,
  StyledBigCalendar,
  StyledTypographyOneLine,
  StyledTypographyTime
} from "./style/BigCalendar.styled"

import { AppContext } from "components/AppContextProvider"
import i18next, { t } from "i18next"
import { EventDialog } from "./components/CalendarDialog"
import CustomMonthView from "./components/CustomMonthView"
import { ToolbarCalendar } from "./components/ToolbarCalendar"
import { backgroundStatusColor, statusColor, statusIcon, statusIconColor } from "./helpers/calendarStatusEvent"
import { ICalendarProps } from "./interfaces/calendar.interface"
import { IEventProps } from "./interfaces/event.interface"
import { VehicleCheckStatus } from "helpers/Checks/ChecksStatusColor"
import { faLoader } from "@fortawesome/pro-solid-svg-icons"
import { styled } from "@mui/material/styles"

moment.locale(i18next.language || window.localStorage.i18nextLng, {
  week: {
    dow: 1,
    doy: 1
  }
})

const localizer = momentLocalizer(moment)

const getComponents = (currentView: View) => ({
  event: (props: any) => {
    const event = props?.event
    const isMonthView = currentView === "month"

    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="2px"
        sx={{
          height: "100%",
          width: "100%",
          padding: "4px 6px",
          borderRadius: "8px",
          backgroundColor: backgroundStatusColor(event?.typeId, event?.status),
          "&:hover": {
            border: "1px solid " + statusColor(event?.typeId, event?.status),
          },
          overflow: "hidden",
        }}
      >
        {!isMonthView && (
          <Box display="flex" alignItems="center" gap="4px">
            <FontAwesomeIcon
              style={{
                flexShrink: 0,
                transform: event?.status === VehicleCheckStatus.Waiting ? "scaleX(-1)" : ""
              }}
              size="sm"
              color={statusIconColor(event?.typeId, event.status)}
              icon={statusIcon(event.typeId, event?.status) as IconProp}
            />
            <StyledTypographyTime variant="labelSmall" color="grey" sx={{ whiteSpace: "nowrap" }}>
              {event.start && moment(event.start).format("HH:mm")}
            </StyledTypographyTime>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap="4px">
          {isMonthView && (
            <FontAwesomeIcon
              style={{
                flexShrink: 0,
                transform: event?.status === VehicleCheckStatus.Waiting ? "scaleX(-1)" : ""
              }}
              size="sm"
              color={statusIconColor(event?.typeId, event.status)}
              icon={statusIcon(event.typeId, event?.status) as IconProp}
            />
          )}
          <StyledTypographyOneLine noWrap variant="labelSmall">
            {event.subtitle}
          </StyledTypographyOneLine>
        </Box>
        <ShortTypography variant="labelSmall" fontWeight={550} sx={{ wordBreak: "break-word" }}>
          {event.title}
        </ShortTypography>
      </Box>
    )
  },
  toolbar: ToolbarCalendar,
  week: {
    header: ({ date, localizer }: any) => (
      <>
        <Typography>{localizer.format(date, "ddd")}</Typography>
        <Typography color="textPrimary" fontWeight={600}>
          {localizer.format(date, "DD")}
        </Typography>
      </>
    )
  }
}) as Components

export interface EventDialogProps {
  event?: IEventProps
  open: boolean
  onClose: (value: any) => void
  reloadCalendarEvents?: () => void
}

const StyledSpinnerBox = styled(Box)({
  position: "absolute",
  left: "50%",
  top: "46%",
  transform: "translate(-50%, -50%)",
  zIndex: "5",
})

const BigCalendar: React.FC<ICalendarProps> = ({ reloadCalendarEvents }) => {
  const { date, view = "month", setView, setDate, columnView, filterEvents, loadingEvents } = useContext(CalendarContext)
  const { selectedLanguage } = useContext(AppContext)
  const [filterEventsState, setfilterEventsState] = useState<any[]>([])
  const [eventDialog, setEventDialog] = useState<EventDialogProps | null>(null)
  const theme = useTheme()

  useEffect(() => {
    const eventsWithDates = filterEvents.map((event: any) => {
      const startDate = event.start ? moment(event.start).toDate() : undefined
      let endDate = event.end ? moment(event.end).toDate() : undefined
      if (startDate && (!endDate || startDate.getTime() === endDate.getTime())) {
        endDate = moment(startDate).add(1, "hour").toDate()
      }

      return {
        ...event,
        start: startDate,
        end: endDate,
        allDay: false
      }
    })
    setfilterEventsState(eventsWithDates)
  }, [filterEvents])

  const views = useMemo(
    () => ({
      month: columnView ? CustomMonthView : CalendarListView,
      week: columnView ? true : CalendarListView,
      day: columnView ? true : CalendarListView
    }),
    [columnView]
  )

  return (
    <>
      {loadingEvents && (
        <StyledSpinnerBox>
          <Tab icon={<FontAwesomeIcon spin icon={faLoader} style={{
            width: "20%",
            height: "20%",
          }} />} label={t("vehicleregistry.loading_calendar", "Loading calendar")} />
        </StyledSpinnerBox>
      )}
      <StyledBigCalendar
        events={filterEventsState}
        components={getComponents(view)}
        localizer={localizer}
        scrollToTime={new Date(new Date().setHours(7, 0, 0, 0))}
        view={view}
        date={date}
        // @ts-ignore //Fel typ i big-react-calendar interface, om det är löst i kommande version, ta bort ignore
        views={views}
        timeslots={2}
        step={30}
        culture={selectedLanguage}
        className={view === "week" ? "rbc-week-view" : ""}
        eventPropGetter={(event: any) => CustomEvent(event, view, theme)}
        dayLayoutAlgorithm={"no-overlap"}
        onNavigate={(date) => {
          setView("day")
          setDate(date)
        }}
        popup={true}
        messages={{
          showMore: (count) => `+${count} ${t("global.more", "More")?.toLowerCase()}`
        }}
        onSelectEvent={(event) =>
          setEventDialog({
            reloadCalendarEvents: reloadCalendarEvents,
            event: event,
            open: true,
            onClose: () => setEventDialog(prevState => prevState && ({ ...prevState, open: false, }))
          })
        }
      />
      {eventDialog && <EventDialog {...eventDialog} reloadCalendarEvents={reloadCalendarEvents} />}
    </>
  )
}
const CustomEvent = (event: any, view?: View, theme?: any): { style: React.CSSProperties } => {
  var style: React.CSSProperties = {
    backgroundColor: view === "month" ? "transparent" : "rgba(217, 238, 244, 0.41)",
    borderRadius: "8px",
    color: theme.palette.primary.dark,
    border: "8px",
    display: "block",
    left: "0px!important",
    width: "100%!important"
  }
  return {
    style: style
  }
}
export default BigCalendar
